import { createError, RelodeApiError } from "@relode/core";
import {
    convertMarketResearchCompensationResponse,
    convertMarketResearchDiversityResponse,
    convertMarketResearchSummaryResponse,
    convertMarketResearchSupplyDemandResponse,
} from "@relode/core/backend/marketResearch";
import * as React from "react";

import { jobsPublicService } from "../../../../shared/backend/controllers";
import { convertJobPublicMarketResearchResponse } from "../../../../shared/backend/converters";
import {
    ClientPublicJobActionTypes,
    creators,
} from "./clientPublicJobActionTypes";
import { ClientPublicJobState } from "./clientPublicJobState";

export const useClientPublicJobActions = (
    state: ClientPublicJobState,
    dispatch: React.Dispatch<ClientPublicJobActionTypes>
) => {
    const loadJob = async (token: string) => {
        try {
            dispatch(creators.loadJob());
            const response = await jobsPublicService.getJob(token);
            const result = convertJobPublicMarketResearchResponse(response);

            dispatch(creators.loadJobSuccess(result, token));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadJobFailure(err));
                }
            }
            throw createError(err, "client.public.jobActions.loadJob");
        }
    };

    const loadMarketResearchCompensation = async (token: string) => {
        dispatch(creators.loadMarketResearchCompensation());
        try {
            const response =
                await jobsPublicService.getMarketResearchCompensation(token);
            const data = convertMarketResearchCompensationResponse(response);
            dispatch(creators.loadMarketResearchCompensationSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchCompensationFailure(err));
                return;
            }
            throw createError(
                err,
                "client.public.jobActions.loadMarketResearchCompensation"
            );
        }
    };

    const loadMarketResearchSupplyDemand = async (token: string) => {
        dispatch(creators.loadMarketResearchSupplyDemand());
        try {
            const response =
                await jobsPublicService.getMarketResearchSupplyDemand(token);
            const data = convertMarketResearchSupplyDemandResponse(response);

            dispatch(creators.loadMarketResearchSupplyDemandSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchSupplyDemandFailure(err));
                return;
            }
            throw createError(
                err,
                "client.public.jobActions.loadMarketResearchSupplyDemand"
            );
        }
    };

    const loadMarketResearchDiversity = async (token: string) => {
        dispatch(creators.loadMarketResearchDiversity());
        try {
            const response = await jobsPublicService.getMarketResearchDiversity(
                token
            );

            const data = convertMarketResearchDiversityResponse(response);
            dispatch(creators.loadMarketResearchDiversitySuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchDiversityFailure(err));
                return;
            }
            throw createError(
                err,
                "client.public.jobActions.loadMarketResearchDiversity"
            );
        }
    };

    const loadMarketResearchSummary = async (token: string) => {
        dispatch(creators.loadMarketResearchSummary());
        try {
            const response = await jobsPublicService.getMarketResearchSummary(
                token
            );

            const summary = convertMarketResearchSummaryResponse(response);
            
            dispatch(creators.loadMarketResearchSummarySuccess(summary));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchSummaryFailure(err));
                return;
            }
            throw createError(
                err,
                "client.public.jobActions.loadMarketResearchSummary"
            );
        }
    };
    return {
        loadJob,
        loadMarketResearchSummary,
        loadMarketResearchDiversity,
        loadMarketResearchSupplyDemand,
        loadMarketResearchCompensation,
    };
};

export type ClientPublicJobActions = ReturnType<
    typeof useClientPublicJobActions
>;
